// ─── La scheda del socio: anagrafica, borsellino, attività ───────────────────
//
// 🗂️ 27/10/2026 (mockup «Scheda del socio a sezioni», A): i riquadri stanno in cinque sezioni da toccare
// (`SezioniDellaScheda.tsx`): Dati · Livello · Bot · Tessera e soldi · Attività.
//
// 🎯 Qui sta l'altra metà del criterio della Fase 5: «si ricarica un borsellino
// e il saldo si muove subito; si preme due volte ricarica e il movimento resta
// UNO».

import { DisdetteDelSocio } from './DisdetteDelSocio.js';
import { TesseraDelSocio } from './TesseraDelSocio.js';
import { PromoterDelSocio } from './PromoterDelSocio.js';
import { AmiciDelSocio } from './AmiciDelSocio.js';
import { ValutazioneAkinator } from './ValutazioneAkinator.js';
import { useEffect, useRef, useState } from 'react';
import {
  ETICHETTE_MOVIMENTO_PREMIO,
  ETICHETTE_VERSO,
  FASCE_DEL_GIORNO,
  FASCE_DEL_WEEKEND,
  formatCents,
  livelloDaSalvare,
  livelloInParole,
  PAROLE_AMICI,
  PAROLE_APPUNTAMENTI,
  PAROLE_FORMAZIONE,
  PAROLE_LEZIONI_MIE,
  PAROLE_PARTITE,
  PAROLE_RUOLO,
  preferenzeDelBot,
  sessoDeiTipi,
  type FasciaDelGiorno,
  type FasciaDelWeekend,
  type TipoDiPartitaDelSocio,
  parseCents,
  parolaDelLivello,
  sceltaDelLivello,
  segnoDelVerso,
  stessoNome,
  type MovimentoBorsellino,
  type SceltaDiLivello,
  type Socio,
  type StoriaDelSocio,
} from '@pmo/shared';
import {
  usaArchiviazione,
  usaAttivita,
  usaCancellazioneSocio,
  usaCancellazioneConStoria,
  usaBorsellino,
  usaDisponibilitaDelSocio,
  usaModificaSocio,
  usaMovimentoBorsellino,
  usaPremio,
  usaScalaDeiLivelli,
  usaSessoControllato,
  SESSO_A_PAROLE,
  usaSocio,
  usaStoriaDelSocio,
  usaCancellazioneChiesta,
  usaAzzeraLivello,
  usaAzzeraLivelloDisponibile,
  usaNotificheDiProva,
  usaStornoMovimento,
} from '../../features/soci/dati.js';
import { etichettaTipo } from '../../features/calendario/Griglia.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { InvitoBot } from './InvitoBot.js';
import { CampoTelefono } from './CampoTelefono.js';
import { StoricoDeiDati } from './StoricoDeiDati.js';
import {
  LivelloDellaScheda,
  Linguette,
  RigaDelSocio,
  usaSezione,
  type Sezione,
} from './SezioniDellaScheda.js';

export function SchedaSocio({ id, chiudi }: { id: string; chiudi: () => void }): React.JSX.Element {
  const foglio = useRef<HTMLDialogElement>(null);
  const socio = usaSocio(id);
  const { profilo } = useSessione();

  // `showModal()` e non l'attributo `open`: è la differenza fra un riquadro che
  // sembra un dialogo e un dialogo vero — sfondo, `Esc`, e il resto della
  // pagina che non si può più toccare per sbaglio.
  useEffect(() => {
    foglio.current?.showModal();
  }, []);

  const puòVedereBorsellino = profilo?.permissions.includes('view_members_borsellino') ?? false;
  const puòMuovereDenaro = profilo?.permissions.includes('manage_payments') ?? false;
  const puòModificare = profilo?.permissions.includes('view_members_anagrafica') ?? false;
  const puòVedereAttivita = profilo?.permissions.includes('view_members_attivita') ?? false;
  // 🗑️ Scelta 3A (28/09/2026): cancellare è del titolare e degli amministratori.
  const puòCancellare = profilo?.role === 'owner' || profilo?.role === 'admin';
  // 🗑️ 29/09/2026 (mockup «Cancellare un socio», scelta 2): una scheda CON la storia, solo il titolare.
  const puòCancellareConLaStoria = profilo?.role === 'owner';
  // 🗂️ Le sezioni che hanno qualcosa per questo ruolo: «Attività» c'è solo con almeno un suo riquadro.
  const sezioni: Sezione[] = ['dati', 'livello', 'bot', 'soldi'];
  if (puòVedereAttivita || puòModificare || puòCancellare) sezioni.push('attivita');
  const [sezione, scegli] = usaSezione(sezioni);

  return (
    <dialog className="foglio scheda-socio" ref={foglio} onClose={chiudi}>
      {socio.isPending ? (
        <Attesa cosa="la scheda" />
      ) : socio.error ? (
        <Guasto errore={socio.error} />
      ) : socio.data ? (
        <>
          <header>
            <h2>{socio.data.displayName}</h2>
            <button type="button" className="piatto" onClick={() => foglio.current?.close()}>
              Chiudi
            </button>
          </header>

          {socio.data.archivedAt && (
            <p className="tenue piccolo">
              Archiviato il {new Date(socio.data.archivedAt).toLocaleDateString('it-IT')}. Resta tutto quello
              che ha fatto: un socio archiviato non è un socio cancellato.
            </p>
          )}

          {/* 🗂️ 27/10/2026, mockup «Scheda del socio a sezioni», A: la riga sotto il nome e le sezioni da toccare. */}
          <RigaDelSocio
            socio={socio.data}
            vedeAnagrafica={puòModificare}
            vedeBorsellino={puòVedereBorsellino}
          />
          <Linguette ci={sezioni} scelta={sezione} scegli={scegli} />

          <div
            id="sezione-aperta"
            role="tabpanel"
            aria-labelledby={`sezione-${sezione}`}
            className="sezione-aperta"
          >
            {sezione === 'dati' && (
              <>
                <Anagrafica socio={socio.data} modificabile={puòModificare} />
                {/* 🗓️ 16/10/2026: quando gioca e che partite vuole, da toccare come sul bot. */}
                <Disponibilita socio={socio.data} modificabile={puòModificare} />
                {/* 🕘 B1 · 3A (29/09/2026): nome, cognome e numero, prima → dopo, e chi. */}
                {puòModificare && <StoricoDeiDati id={id} />}
              </>
            )}
            {sezione === 'livello' && (
              <>
                <LivelloDellaScheda socio={socio.data} modificabile={puòModificare} />
                {/* 🎾 22/10/2026: il livello cambiato dalla Segreteria, mandato a Level Akinator. */}
                <ValutazioneAkinator id={id} modificabile={puòModificare} />
                {/* 🧪 14/10/2026: solo per le prove, solo sulle schede di Maurizio Aprea, Marco Natalini e Luca D’Abruzzo. */}
                {puòCancellareConLaStoria && <AzzeraIlLivello socio={socio.data} />}
              </>
            )}
            {sezione === 'bot' && (
              <>
                {/* 🤖 L'invito al bot dei soci (24/09/2026). */}
                {puòModificare && (
                  <InvitoBot id={id} nome={socio.data.firstName} nomeIntero={socio.data.displayName} />
                )}
                <DalBot socio={socio.data} />
                {/* 👥 27/10/2026, mockup «Amici nel gestionale», 3A: gli amici del bot, solo da leggere. */}
                <AmiciDelSocio id={id} />
              </>
            )}
            {sezione === 'soldi' && (
              <>
                {/* 🪪 16/10/2026: la tessera associativa, registrata dalla mail di WindDoc. */}
                <TesseraDelSocio id={id} modificabile={puòModificare} puoIncassare={puòMuovereDenaro} />
                <PromoterDelSocio id={id} modificabile={puòModificare} />
                {/* 🚦 Fase B (28/09/2026): libero o obbligo carta, le infrazioni. */}
                <DisdetteDelSocio id={id} puòCorreggere={puòMuovereDenaro} />
                {puòVedereBorsellino && <Borsellino id={id} puòMuovere={puòMuovereDenaro} />}
              </>
            )}
            {sezione === 'attivita' && (
              <>
                {puòVedereAttivita && <Attivita id={id} />}
                {puòModificare && <Archiviazione socio={socio.data} />}
                {puòCancellare && (
                  <Cancellazione
                    socio={socio.data}
                    anche={puòCancellareConLaStoria}
                    chiudi={() => foglio.current?.close()}
                  />
                )}
              </>
            )}
          </div>
        </>
      ) : null}
    </dialog>
  );
}

/**
 * 🗓️ **Le disponibilità e i tipi di partita** (16/10/2026, mockup «🗓️
 * Disponibilità nella scheda», approvato). Le stesse scelte che il socio fa sul
 * bot, in «👤 Profilo › 🗓️ Disponibilità», qui da toccare: ogni tocco si salva
 * subito, e il socio le ritrova uguali nel bot, che le rilegge a ogni giro.
 *
 * ⚖️ Il riquadro c'è sempre, anche quando il socio non ha scelto niente: allora
 * nessuna scelta è accesa, e la riga lo dice. Le fasce possono restare tutte
 * spente (lì non gioca); i tipi di partita no, come sul bot: almeno uno, e da una
 * parte sola — un tipo dell'altra parte prende il posto di quelli di prima.
 * 🌙 Nel weekend il bot offre solo mattina e pomeriggio: una sera scritta prima
 * non si mostra, e cade alla prima modifica, come quando la riscrive il socio.
 * 👫 16/10/2026 («Il misto è misto»): sullo schermo il misto è **uno**. I dati
 * restano `MM`/`MF`: «Misto» diventa `MM` per un uomo e `MF` per una donna. Col
 * sesso in scheda si vede solo la sua parte; senza, Maschile · Femminile · Misto,
 * e il misto da solo prende la parte già scelta — se non ce n'è, lo dice.
 */
function Disponibilita({ socio, modificabile }: { socio: Socio; modificabile: boolean }): React.JSX.Element {
  const salva = usaDisponibilitaDelSocio(socio.id);
  const [avviso, setAvviso] = useState<string | null>(null);
  const p = preferenzeDelBot(socio.preferences, socio.gender);
  const settimana = p.availability?.weekday ?? [];
  const weekend = (p.availability?.weekend ?? []).filter((f): f is FasciaDelWeekend =>
    (FASCE_DEL_WEEKEND as readonly string[]).includes(f),
  );
  const tipi = p.matchTypes ?? [];
  // 👫 La parte del misto: il sesso della scheda, altrimenti quella dei tipi già scelti.
  const parte = socio.gender ?? sessoDeiTipi(tipi);
  const misto: TipoDiPartitaDelSocio | null = parte === 'M' ? 'MM' : parte === 'F' ? 'MF' : null;
  const bottoniDeiTipi: readonly ('M' | 'F' | 'misto')[] =
    socio.gender === 'M' ? ['M', 'misto'] : socio.gender === 'F' ? ['F', 'misto'] : ['M', 'F', 'misto'];
  const fermo = !modificabile || salva.isPending;
  const nonScelto = <span className="tenue piccolo">non ha ancora scelto</span>;

  const scambia = <T,>(lista: readonly T[], x: T): T[] =>
    lista.includes(x) ? lista.filter((y) => y !== x) : [...lista, x];

  function toccaTipo(t: TipoDiPartitaDelSocio): void {
    if (tipi.includes(t)) {
      // ✋ L'ultimo tipo resta: senza, il circolo non saprebbe che partite proporgli.
      if (tipi.length === 1) {
        setAvviso('Tienine almeno uno.');
        return;
      }
      setAvviso(null);
      salva.mutate({ matchTypes: tipi.filter((x) => x !== t) });
      return;
    }
    setAvviso(null);
    // 👨/👩 Maschile e femminile non vanno insieme: l'altra parte prende il posto di quella di prima.
    const stessaParte = sessoDeiTipi([...tipi, t]) !== null;
    salva.mutate({ matchTypes: stessaParte ? [...tipi, t] : [t] });
  }

  function toccaMisto(): void {
    // 👫 Senza sesso e senza una parte scelta, il misto non sa di chi è: non si manda niente.
    if (!misto) {
      setAvviso('Prima scegli Maschile o Femminile, oppure il Sesso del socio.');
      return;
    }
    toccaTipo(misto);
  }

  return (
    <section className="riquadro disponibilita-socio">
      <h3>🗓️ Disponibilità</h3>
      <div className="riga-disponibilita">
        <span>Settimana</span>
        <div className="scelta-a-bottoni" role="group" aria-label="Settimana">
          {FASCE_DEL_GIORNO.map((f) => (
            <button
              key={f}
              type="button"
              className={settimana.includes(f) ? 'scelto' : undefined}
              aria-pressed={settimana.includes(f)}
              disabled={fermo}
              onClick={() => salva.mutate({ weekday: scambia(settimana, f) })}
            >
              {NOMI_DELLE_FASCE[f]}
            </button>
          ))}
        </div>
        {!p.availability && nonScelto}
      </div>
      <div className="riga-disponibilita">
        <span>Weekend</span>
        <div className="scelta-a-bottoni" role="group" aria-label="Weekend">
          {FASCE_DEL_WEEKEND.map((f) => (
            <button
              key={f}
              type="button"
              className={weekend.includes(f) ? 'scelto' : undefined}
              aria-pressed={weekend.includes(f)}
              disabled={fermo}
              onClick={() => salva.mutate({ weekend: scambia(weekend, f) })}
            >
              {NOMI_DELLE_FASCE[f]}
            </button>
          ))}
        </div>
        {!p.availability && nonScelto}
      </div>
      <div className="riga-disponibilita">
        <span>Tipo di partita</span>
        <div className="scelta-a-bottoni" role="group" aria-label="Tipo di partita">
          {bottoniDeiTipi.map((b) => {
            const acceso = b === 'misto' ? misto !== null && tipi.includes(misto) : tipi.includes(b);
            return (
              <button
                key={b}
                type="button"
                className={acceso ? 'scelto' : undefined}
                aria-pressed={acceso}
                disabled={fermo}
                onClick={() => (b === 'misto' ? toccaMisto() : toccaTipo(b))}
              >
                {NOMI_DEI_TIPI[b]}
              </button>
            );
          })}
        </div>
        {!p.matchTypes && nonScelto}
        {avviso && <span className="tenue piccolo">{avviso}</span>}
      </div>
      <p className="tenue piccolo">Scelta dal socio sul bot · ogni modifica qui la vede anche lui nel bot.</p>
      <Guasto errore={salva.error} />
    </section>
  );
}

/** 🗓️ Le parole delle scelte, come nel riquadro approvato. */
const NOMI_DELLE_FASCE: Record<FasciaDelGiorno, string> = {
  mattina: 'Mattina',
  pranzo: 'Pranzo',
  pomeriggio: 'Pomeriggio',
  sera: 'Sera',
};
/** 👫 16/10/2026 («Il misto è misto»): un misto solo, per tutti e due. */
const NOMI_DEI_TIPI: Record<'M' | 'F' | 'misto', string> = {
  M: 'Maschile',
  F: 'Femminile',
  misto: 'Misto',
};

/**
 * 🤖 **Quello che il socio ha scelto sul bot** (25/09/2026, voci 12 e 13): cosa
 * vuole ricevere, e a che punto è. Lo sceglie lui, dal «👤 Profilo» del bot; qui
 * si legge e basta.
 *
 * ⚖️ «Non ha ancora scelto» si scrive così, e non è «no»: finché non sceglie,
 * le notifiche non gli arrivano.
 * 🔄 16/10/2026: le fasce e i tipi di partita sono passati in «🗓️ Disponibilità»,
 * dove si toccano; tornei, eventi e lezioni sono usciti dal bot, e qui con lui.
 * Le righe rimaste hanno i nomi del bot: «🙋 Proposte della Segreteria» e
 * «⏰ Promemoria prima di giocare».
 * Senza nessuna scelta il riquadro non c'è: non c'è niente da leggere.
 */
function DalBot({ socio }: { socio: Socio }): React.JSX.Element | null {
  const p = preferenzeDelBot(socio.preferences);
  const n = p.notifications;
  if (
    !n.matches &&
    !n.appointments &&
    !n.friends &&
    !n.lineup &&
    !n.lessonReminders &&
    !n.lessonLineup &&
    !p.role
  )
    return null;
  const nonScelto = 'non ha ancora scelto';
  // 🆕 28/09/2026 (Partite Aperte, scelta 1A): per il bot il test è fatto con un
  // livello in scheda oppure con una prova consegnata. Stessa regola del ponte.
  const testFatto = socio.level !== null || socio.hasAssessment;
  return (
    <section className="riquadro">
      <h3>Scelto sul bot</h3>
      <dl className="piccolo">
        <dt>Nel bot</dt>
        <dd>{p.role ? PAROLE_RUOLO[p.role] : nonScelto}</dd>
        <dt>Scopri il tuo livello di gioco</dt>
        <dd>{testFatto ? '✅ fatto' : '⏳ da fare: l’assistente glielo chiede a ogni gesto'}</dd>
        <dt>🙋 Proposte della Segreteria</dt>
        <dd>{n.matches ? PAROLE_PARTITE[n.matches] : nonScelto}</dd>
        {/* 🗑️ 20/10/2026 (bot, «Notifiche in ordine»): via la riga «Livelli», le proposte sono del suo livello soltanto. */}
        <dt>⏰ Promemoria prima di giocare</dt>
        <dd>{n.appointments ? PAROLE_APPUNTAMENTI[n.appointments] : nonScelto}</dd>
        {/* 🔔 18/10/2026 (bot, «Notifiche in tre»): le due scelte nuove, coi valori di partenza del bot. */}
        <dt>👥 Inviti degli amici</dt>
        <dd>{n.friends ? PAROLE_AMICI[n.friends] : `${nonScelto} (vale «no»)`}</dd>
        <dt>🎾 Chi entra e chi esce</dt>
        <dd>{n.lineup ? PAROLE_FORMAZIONE[n.lineup] : `${nonScelto} (vale «sì»)`}</dd>
        {/* 🎓 18/10/2026 (bot, «Home e Lezioni»): «🎓 Le mie lezioni», lezioni e allenamenti. */}
        <dt>🎓 Promemoria delle lezioni</dt>
        <dd>{n.lessonReminders ? PAROLE_LEZIONI_MIE[n.lessonReminders] : `${nonScelto} (vale «sì»)`}</dd>
        <dt>🎓 Chi entra e chi esce dalle lezioni</dt>
        <dd>{n.lessonLineup ? PAROLE_LEZIONI_MIE[n.lessonLineup] : `${nonScelto} (vale «sì»)`}</dd>
      </dl>
    </section>
  );
}

/**
 * ⭐⭐ **L'IDENTIFICATIVO DEL SOCIO, in chiaro e copiabile** — 17/09/2026,
 * richiesta sua: *«Fammi vedere l'ID del socio nella scheda»*.
 *
 * 🔑 **È l'id NOSTRO**, quello che il database genera e che non cambia mai:
 * cambiando nome, telefono o email resta lo stesso, ed è con questo — non col
 * nome — che una persona è legata alle sue partite, ai suoi incassi e al suo
 * borsellino. È anche quello che si legge nell'indirizzo della pagina
 * (`/soci?socio=…`), quindi mostrarlo non svela niente che non fosse già lì.
 *
 * ⛔ **E NON è il codice con cui il socio è arrivato dal vecchio gestionale**
 * (`external_ref`): quello non esce da nessuna rotta, apposta. Il giorno in cui
 * una schermata lo mostrasse, qualcuno comincerebbe a usarlo per riconoscere
 * qualcuno, e l'identità sarebbe tornata a essere quella del sistema che stiamo
 * spegnendo.
 *
 * ⚖️ **Perché c'è il bottone e non solo il testo.** Un identificativo si legge
 * per riscriverlo altrove — in una segnalazione, in una ricerca, in un
 * messaggio. Trentasei caratteri ricopiati a mano si sbagliano, e un id
 * sbagliato di un carattere non dà errore: indica **un'altra persona** o
 * nessuna. ⇒ Il bottone c'è perché l'alternativa non è «copiare a mano», è
 * «copiare a mano un carattere storto».
 *
 * ⚠️ `navigator.clipboard` non c'è sempre (contesti non sicuri, permesso
 * negato): se non funziona **non si finge**. Il testo resta selezionabile, e
 * il bottone lo dice invece di lasciar credere che sia stato copiato.
 */
function Identificativo({ id }: { id: string }): React.JSX.Element {
  const [esito, setEsito] = useState<'fermo' | 'copiato' | 'nolo'>('fermo');

  useEffect(() => {
    if (esito === 'fermo') return;
    const t = setTimeout(() => setEsito('fermo'), 2500);
    return () => clearTimeout(t);
  }, [esito]);

  return (
    <span className="identificativo">
      <code>{id}</code>
      <button
        type="button"
        className="piatto"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(id)
            .then(() => setEsito('copiato'))
            .catch(() => setEsito('nolo'));
          if (!navigator.clipboard) setEsito('nolo');
        }}
      >
        {esito === 'copiato' ? 'Copiato' : esito === 'nolo' ? 'Copialo a mano' : 'Copia'}
      </button>
    </span>
  );
}

// ── L'anagrafica ────────────────────────────────────────────────────────────

/**
 * 🙋 **Il sesso nella scheda** (16/10/2026, «Trovami N giocatori»). Lo scrive la
 * Segreteria, o il bot dai tipi di partita scelti dal socio: allora resta
 * «🟡 Scritto dal bot: da controllare» finché qualcuno non tocca «✓ Controllato»
 * (o lo corregge da «Modifica l'anagrafica»).
 */
function Sesso({ socio, modificabile }: { socio: Socio; modificabile: boolean }): React.JSX.Element {
  const controllato = usaSessoControllato(socio.id);
  return (
    <>
      {socio.gender ? SESSO_A_PAROLE[socio.gender] : 'Non indicato'}
      {socio.genderToCheck && (
        <>
          <span className="sesso-da-controllare">🟡 Scritto dal bot: da controllare</span>
          {modificabile && (
            <button
              type="button"
              className="piatto"
              disabled={controllato.isPending}
              onClick={() => controllato.mutate()}
            >
              ✓ Controllato
            </button>
          )}
        </>
      )}
      <Guasto errore={controllato.error} />
    </>
  );
}

function Anagrafica({ socio, modificabile }: { socio: Socio; modificabile: boolean }): React.JSX.Element {
  const salva = usaModificaSocio(socio.id);
  const [aperto, setAperto] = useState(false);
  const [bozza, setBozza] = useState({
    firstName: socio.firstName,
    lastName: socio.lastName,
    gender: socio.gender ?? '',
    email: socio.email ?? '',
    phone: socio.phone ?? '',
    level: socio.level === null ? '' : String(socio.level),
    notes: socio.notes ?? '',
  });
  /**
   * 🎾 Il livello si sceglie a parole (25/09/2026), dalla scala del circolo.
   * `undefined` = non toccato: la tendina segna la fascia del livello di oggi,
   * calcolata quando la scala è arrivata. Senza scala resta il campo numerico
   * di prima — senza parole dichiarate non se ne inventano.
   */
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  const [sceltaToccata, setSceltaToccata] = useState<SceltaDiLivello | undefined>(undefined);
  const scelta = sceltaToccata === undefined ? sceltaDelLivello(fasce, socio.level) : sceltaToccata;
  const fuoriScala =
    fasce.length > 0 && socio.level !== null && parolaDelLivello(fasce, socio.level) === null;

  if (!aperto) {
    return (
      <section className="riquadro dati-socio">
        <dl>
          <dt>Telefono</dt>
          <dd>{socio.phone ?? '—'}</dd>
          <dt>Email</dt>
          <dd>{socio.email ?? '—'}</dd>
          <dt>Sesso</dt>
          <dd>
            <Sesso socio={socio} modificabile={modificabile} />
          </dd>
          <dt>Livello</dt>
          <dd>
            {livelloInParole(fasce, socio.level)}
            {/* Un numero che la scala non chiama (il vecchio 0,5) si vede, in
                tenue: «Da definire» da solo nasconderebbe che un numero c'è. */}
            {fuoriScala && socio.level !== null && (
              <span className="tenue"> ({socio.level.toFixed(1).replace('.', ',')})</span>
            )}
          </dd>
          <dt>Note</dt>
          <dd>{socio.notes ?? '—'}</dd>
          {/* 🆕 28/09/2026 (registrazione dall'assistente, scelta 3A): da dove
              viene la scheda. Il numero l'ha dato Telegram, non una persona. */}
          <dt>Iscritto</dt>
          <dd>
            {socio.fromAssistant ? (
              <>
                <span className="dall-assistente">🆕 dall’assistente</span> {quandoIscritto(socio.createdAt)}{' '}
                · numero verificato da Telegram
              </>
            ) : (
              'dalla Segreteria'
            )}
          </dd>
          <dt>ID</dt>
          <dd>
            <Identificativo id={socio.id} />
          </dd>
        </dl>
        {modificabile && (
          <button type="button" className="piatto" onClick={() => setAperto(true)}>
            Modifica l’anagrafica
          </button>
        )}
      </section>
    );
  }

  return (
    <form
      className="riquadro dati-socio"
      onSubmit={(e) => {
        e.preventDefault();
        salva.mutate(
          {
            firstName: bozza.firstName,
            lastName: bozza.lastName,
            // 🚨 Stringa vuota ⇒ `null`, cioè «svuotalo». Mandare `''` farebbe
            // scrivere un'email vuota che il vincolo di unicità considera un
            // valore: due soci «senza email» diventerebbero un doppione.
            email: bozza.email.trim() === '' ? null : bozza.email.trim(),
            phone: bozza.phone.trim() === '' ? null : bozza.phone.trim(),
            // 🙋 Si manda solo se è cambiato: salvare la scheda per un'altra
            // ragione non deve spegnere il «da controllare» del bot.
            ...(bozza.gender !== (socio.gender ?? '')
              ? { gender: bozza.gender === '' ? null : (bozza.gender as 'M' | 'F') }
              : {}),
            // 🚨 La stessa parola rimanda il numero di prima: un 3,5 resta 3,5.
            level:
              fasce.length > 0
                ? livelloDaSalvare(fasce, socio.level, scelta)
                : bozza.level.trim() === ''
                  ? null
                  : Number(bozza.level.replace(',', '.')),
            notes: bozza.notes.trim() === '' ? null : bozza.notes,
          },
          {
            onSuccess: () => {
              setSceltaToccata(undefined);
              setAperto(false);
            },
          },
        );
      }}
    >
      <Campo
        etichetta="Nome"
        required
        value={bozza.firstName}
        onChange={(e) => setBozza({ ...bozza, firstName: e.target.value })}
      />
      <Campo
        etichetta="Cognome"
        required
        value={bozza.lastName}
        onChange={(e) => setBozza({ ...bozza, lastName: e.target.value })}
      />
      <Campo
        etichetta="Email"
        type="email"
        value={bozza.email}
        onChange={(e) => setBozza({ ...bozza, email: e.target.value })}
      />
      <CampoTelefono
        etichetta="Telefono"
        value={bozza.phone}
        onChange={(phone) => {
          // Il rifiuto di prima parlava del numero vecchio: riscrivendolo, sparisce.
          salva.reset();
          setBozza({ ...bozza, phone });
        }}
      />
      <label className="campo">
        <span>Sesso</span>
        <select value={bozza.gender} onChange={(e) => setBozza({ ...bozza, gender: e.target.value })}>
          <option value="">Non indicato</option>
          <option value="M">Uomo</option>
          <option value="F">Donna</option>
        </select>
        {socio.genderToCheck && (
          <small className="tenue">Scritto dal bot: cambiato da qui, diventa controllato.</small>
        )}
      </label>
      {fasce.length > 0 ? (
        <label className="campo">
          <span>Livello</span>
          <select
            value={scelta === null ? '' : String(scelta)}
            onChange={(e) => {
              const v = e.target.value;
              setSceltaToccata(v === '' ? null : v === 'fuori' ? 'fuori' : Number(v));
            }}
          >
            <option value="">Da definire</option>
            {fuoriScala && socio.level !== null && (
              <option value="fuori">{socio.level.toFixed(1).replace('.', ',')} (fuori dalla scala)</option>
            )}
            {fasce.map((f, i) => (
              <option key={f.parola} value={String(i)}>
                {f.parola}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <Campo
          etichetta="Livello"
          inputMode="decimal"
          value={bozza.level}
          onChange={(e) => setBozza({ ...bozza, level: e.target.value })}
        />
      )}
      <label className="campo">
        <span>Note</span>
        <textarea
          rows={2}
          value={bozza.notes}
          onChange={(e) => setBozza({ ...bozza, notes: e.target.value })}
        />
      </label>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button
          type="button"
          className="bordato"
          onClick={() => {
            setSceltaToccata(undefined);
            setAperto(false);
          }}
        >
          Lascia stare
        </button>
      </div>
    </form>
  );
}

// ── Il borsellino ───────────────────────────────────────────────────────────

function Borsellino({ id, puòMuovere }: { id: string; puòMuovere: boolean }): React.JSX.Element {
  const borsellino = usaBorsellino(id);
  const storna = usaStornoMovimento(id);

  return (
    <section className="riquadro borsellino">
      <h3>Borsellino</h3>
      {borsellino.isPending ? (
        <Attesa cosa="il borsellino" />
      ) : (
        <>
          <p className={`saldo ${(borsellino.data?.balanceCents ?? 0) > 0 ? 'saldo-pieno' : 'tenue'}`}>
            {formatCents(borsellino.data?.balanceCents ?? 0)}
          </p>
          {puòMuovere && <Movimento id={id} />}
          <Guasto errore={storna.error} />
          {(borsellino.data?.transactions.length ?? 0) === 0 ? (
            <p className="tenue piccolo">Nessun movimento.</p>
          ) : (
            <table className="movimenti">
              <tbody>
                {borsellino.data?.transactions.map((t) => (
                  <RigaMovimento
                    key={t.id}
                    m={t}
                    puòMuovere={puòMuovere}
                    storna={() => storna.mutate(t.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <CreditiPremio id={id} />
    </section>
  );
}

/**
 * 🎁 **I crediti premio** (voce 33, mockup approvato il 26/09/2026): stanno
 * accanto al saldo in euro ma NON sono euro. Qui non ci sono «Ricarica» né
 * «Togli»: non si prelevano, non si rimborsano, non si passano ad altri. Si
 * usano solo alla cassa, sulla quota di una partita.
 * ⚖️ Il riquadro non compare a chi non ne ha mai avuti, finché il premio è spento.
 */
function CreditiPremio({ id }: { id: string }): React.JSX.Element | null {
  const premio = usaPremio(id);
  const p = premio.data;
  if (!p || (!p.active && p.available === 0 && p.movements.length === 0)) return null;
  const inArrivo = p.incoming.reduce((s, x) => s + x.credits, 0);
  const crediti = (n: number) => `${n} ${Math.abs(n) === 1 ? 'Credito' : 'Crediti'}`;
  const data = (d: string) => d.split('-').reverse().join('/');
  return (
    <div className="crediti-premio">
      <h4>🎁 Crediti premio</h4>
      <p className="tenue piccolo">
        1 Credito = {formatCents(p.creditValueCents)} di sconto sulla quota di una partita.
      </p>
      <p className="saldo">{crediti(p.available)}</p>
      {inArrivo > 0 && (
        <p className="in-arrivo">
          ⏳ {crediti(inArrivo)} in arrivo, a fine partita:{' '}
          {p.incoming.map((x) => `${data(x.date)} ${x.time} · ${x.courtName}`).join(', ')}
        </p>
      )}
      {p.expiring.map((x) => (
        <p key={x.expiresOn} className="tenue piccolo">
          {crediti(x.credits)} scadono il {data(x.expiresOn)}
        </p>
      ))}
      <p className="tenue piccolo">
        Non si prelevano, non si rimborsano e non si passano ad altri soci: si usano alla cassa, quando lo
        chiede lui.
      </p>
      {p.movements.length > 0 && (
        <table className="movimenti">
          <tbody>
            {p.movements.map((m) => (
              <tr key={m.id}>
                <td>
                  {ETICHETTE_MOVIMENTO_PREMIO[m.kind]}
                  {m.booking && (
                    <span className="tenue piccolo">
                      {' '}
                      · {m.booking.replace(/^(\d{4})-(\d{2})-(\d{2})/, '$3/$2')}
                    </span>
                  )}
                  <br />
                  <span className="tenue piccolo">
                    {new Date(m.recordedAt).toLocaleString('it-IT', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </td>
                <td className={`a-destra ${m.credits > 0 ? 'crediti-piu' : 'saldo-rosso'}`}>
                  {m.credits > 0 ? '+' : '−'}
                  {crediti(Math.abs(m.credits))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RigaMovimento({
  m,
  puòMuovere,
  storna,
}: {
  m: MovimentoBorsellino;
  puòMuovere: boolean;
  storna: () => void;
}): React.JSX.Element {
  const segno = segnoDelVerso(m.direction);
  /**
   * 🚨⭐⭐ **LO STORNO CHIEDE CONFERMA, e fino al 18/09/2026 partiva al primo
   * clic.**
   * ⚖️ Nella **cassa** lo stesso gesto ha sempre chiesto «Sì, storna»: stesso
   *   denaro, stessa irreversibilità (una riga di denaro non si cancella: se ne
   *   scrive una contraria), e due livelli di protezione diversi — con quello
   *   scoperto proprio dove il bottone sta **in ogni riga dell'elenco**, a un
   *   dito di distanza da «guardo il registro».
   * 📌 *Una protezione che c'è in un posto e non nell'altro non è una scelta:
   *    è il segno che i due posti sono stati scritti in due momenti diversi.*
   * ⭐ E la domanda dice **cosa** si sta per stornare — verso e cifra — perché
   *   in un elenco di righe che si somigliano la conferma senza il dettaglio
   *   protegge dal clic, non dallo sbaglio.
   */
  const [conferma, setConferma] = useState(false);
  /**
   * 🗣️ **«Fallo uguale nel borsellino»** (18/09/2026). In cassa una riga dice da
   * sé cos'è — `storno` o `stornato` — e il bottone non c'è dove non serve. Qui
   * il bottone c'era **su tutte**, e infatti si poteva stornare uno storno
   * all'infinito: misurato, tre righe da una rettifica sola.
   * ⚖️ Adesso il rifiuto arriva dal server (`STORNO_DI_UNO_STORNO`), ed è lui a
   *   decidere; questa riga toglie il bottone che porterebbe sempre a
   *   quell'errore. 📌 *Un bottone che porta sempre a un errore è peggio di un
   *   bottone che manca: il primo fa credere che il gesto esista.*
   */
  const eUnoStorno = m.reversesTransactionId !== null;
  const giaStornato = m.reversedByTransactionId !== null;
  return (
    <tr className={eUnoStorno ? 'storno' : giaStornato ? 'stornato' : undefined}>
      <td className="tenue piccolo">{new Date(m.recordedAt).toLocaleString('it-IT')}</td>
      <td>{ETICHETTE_VERSO[m.direction]}</td>
      <td className={segno > 0 ? 'saldo-pieno' : 'saldo-rosso'}>
        {segno > 0 ? '+' : '−'}
        {formatCents(m.amountCents)}
      </td>
      <td className="tenue piccolo">{m.reason ?? ''}</td>
      <td>
        {/* ⛔ Un movimento non si cancella: se ne scrive uno uguale e contrario.
            Lo rifiuterebbe anche il trigger del database. */}
        {eUnoStorno ? (
          <span className="tenue piccolo">storno</span>
        ) : giaStornato ? (
          <span className="tenue piccolo">stornato</span>
        ) : m.paymentId !== null ? (
          /**
           * 💶⭐ NASCE DA UNA RIGA DI CASSA (Fase 6): qui il bottone NON c'è, e
           * non è per prudenza — il server rifiuterebbe («si storna dalla
           * cassa, così tornano indietro tutt'e due»).
           * ⚖️ Un bottone che porta sempre a un errore è peggio di un bottone
           * che manca: il primo fa credere che il gesto esista e lascia a chi
           * lavora il compito di scoprire che no.
           */
          <span className="tenue piccolo">dalla cassa</span>
        ) : (
          puòMuovere &&
          (conferma ? (
            <span className="conferma-storno">
              <span className="tenue piccolo">
                Stornare {segno > 0 ? '+' : '−'}
                {formatCents(m.amountCents)}?
              </span>
              <button
                type="button"
                className="pericolo"
                onClick={() => {
                  setConferma(false);
                  storna();
                }}
              >
                Sì
              </button>
              <button type="button" className="piatto" onClick={() => setConferma(false)}>
                No
              </button>
            </span>
          ) : (
            <button type="button" className="piatto" onClick={() => setConferma(true)}>
              Storna
            </button>
          ))
        )}
      </td>
    </tr>
  );
}

function Movimento({ id }: { id: string }): React.JSX.Element {
  const [verso, setVerso] = useState<'recharge' | 'withdraw'>('recharge');
  const [importo, setImporto] = useState('');
  const [motivo, setMotivo] = useState('');
  /**
   * 💶⭐ CON CHE MEZZO IL DENARO PASSA DAL CASSETTO — ed è la riga che fa
   * quadrare la giornata (Fase 6). Una ricarica da 50,00 € in contanti mette
   * 50,00 € nel cassetto: senza questo, a fine serata la segreteria conta
   * cinquanta euro che il riepilogo della cassa non sa spiegare.
   * ⛔ «rettifica» è l'unica scelta che NON tocca la cassa, e si chiama così
   *    apposta: dev'essere una scelta, non una dimenticanza.
   */
  const [mezzo, setMezzo] = useState<'cash' | 'card' | ''>('cash');

  /**
   * 🚨 LA CHIAVE NASCE CON LA FINESTRA, NON COL CLIC — ed è la riga che vince
   * il criterio «si preme due volte e il movimento resta uno».
   *
   * ⚖️ Generandola al clic, due clic farebbero due chiavi e quindi due
   * movimenti. E il bottone disabilitato non basta: non ferma un `Invio`
   * ripetuto, non ferma una rete che rimanda la richiesta, e non ferma due
   * schede aperte sullo stesso socio. L'unica difesa che regge è che i due
   * tentativi portino la stessa chiave, e che a rifiutare il secondo sia il
   * database.
   * ⭐ Si rinnova dopo ogni movimento riuscito: la ricarica DOPO è un gesto
   *   nuovo, e deve poter passare.
   */
  const [chiave, setChiave] = useState(() => crypto.randomUUID());
  const muovi = usaMovimentoBorsellino(id, chiave);

  const cents = parseCents(importo);
  const valido = cents !== null && cents > 0;

  return (
    <form
      className="muovi-borsellino"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        muovi.mutate(
          {
            verso,
            amountCents: cents,
            reason: motivo.trim() === '' ? null : motivo.trim(),
            method: mezzo === '' ? null : mezzo,
          },
          {
            onSuccess: () => {
              setImporto('');
              setMotivo('');
              setChiave(crypto.randomUUID());
            },
          },
        );
      }}
    >
      {/*
       * ⭐ UNA PAROLA SOLA, e non è per brevità: il campo è largo ~9rem in una riga da quattro,
       * e «Togli dal borsellino» non ci sta — il <select> lo TRONCA invece di andare a capo,
       * quindi l'operatore leggeva «Togli dal bor…». Il riquadro si intitola già Borsellino:
       * «dal borsellino» ripeteva l'unica cosa che era già scritta sopra.
       */}
      <label className="campo">
        <span>Gesto</span>
        <select value={verso} onChange={(e) => setVerso(e.target.value as 'recharge' | 'withdraw')}>
          <option value="recharge">Carica</option>
          <option value="withdraw">Togli</option>
        </select>
      </label>
      <Campo
        etichetta="Importo (€)"
        inputMode="decimal"
        placeholder="10,00"
        value={importo}
        onChange={(e) => setImporto(e.target.value)}
      />
      {/*
       * ⚠️ Stessa misura, stesso difetto: «Rettifica (non tocca la cassa)» è
       * lungo 232px in una casella da 108 — l'operatore ne leggeva un terzo.
       * ⚖️ La spiegazione però NON si butta: è l'unica cosa che distingue una
       * correzione da un movimento di cassa vero. Va sotto, e compare solo
       * quando serve — cioè quando la rettifica è quella scelta.
       */}
      <label className="campo">
        <span>Mezzo</span>
        <select value={mezzo} onChange={(e) => setMezzo(e.target.value as 'cash' | 'card' | '')}>
          <option value="cash">Contanti</option>
          <option value="card">Carta</option>
          <option value="">Rettifica</option>
        </select>
      </label>
      <Campo etichetta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <Guasto errore={muovi.error} />
      <button type="submit" disabled={!valido || muovi.isPending}>
        {muovi.isPending ? 'Un attimo…' : verso === 'recharge' ? 'Carica' : 'Togli'}
      </button>
      {mezzo === '' && (
        <p className="tenue piccolo">La rettifica corregge il borsellino e non tocca la cassa.</p>
      )}
      {importo.trim() !== '' && !valido && <p className="guasto">Scrivi un importo, per esempio 10,00.</p>}
    </form>
  );
}

// ── L'attività ──────────────────────────────────────────────────────────────

function Attivita({ id }: { id: string }): React.JSX.Element {
  const attivita = usaAttivita(id);
  if (attivita.isPending) return <Attesa cosa="l’attività" />;
  const b = attivita.data?.bookings ?? [];
  const p = attivita.data?.payments ?? [];

  return (
    <section className="riquadro attivita">
      <h3>Attività</h3>
      {b.length === 0 ? (
        <p className="tenue piccolo">Non è mai stato in campo.</p>
      ) : (
        <table>
          <tbody>
            {b.slice(0, 10).map((r) => (
              <tr key={r.bookingId}>
                <td className="tenue piccolo">{r.date}</td>
                <td>{r.time}</td>
                <td>{r.courtName}</td>
                {/* 🔄 Una prenotazione si chiama col suo tipo, dal 17/09/2026
                    (⇒ `pezzi-scheda.tsx`): qui c'era `r.title ?? r.type`, che
                    avrebbe fatto riaffiorare i titoli vecchi proprio dove non
                    li cerca nessuno. */}
                <td>{etichettaTipo(r.type)}</td>
                <td>{r.amountDueCents === null ? '' : formatCents(r.amountDueCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {p.length > 0 && (
        <>
          <h4>Incassi</h4>
          <table>
            <tbody>
              {p.slice(0, 10).map((r) => (
                <tr key={r.id} className={r.status === 'voided' ? 'spento' : ''}>
                  <td className="tenue piccolo">{r.businessDate}</td>
                  <td>{r.method}</td>
                  <td>{formatCents(r.amountCents)}</td>
                  <td className="tenue piccolo">{r.status === 'voided' ? 'stornato' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// ── Archiviare ──────────────────────────────────────────────────────────────

function Archiviazione({ socio }: { socio: Socio }): React.JSX.Element {
  const gesto = usaArchiviazione(socio.id);
  const [chiedo, setChiedo] = useState(false);
  const archiviato = socio.archivedAt !== null;

  if (archiviato) {
    return (
      <section className="riquadro">
        <Guasto errore={gesto.error} />
        <button
          type="button"
          className="piatto"
          disabled={gesto.isPending}
          onClick={() => gesto.mutate('restore')}
        >
          Rimetti fra i soci
        </button>
      </section>
    );
  }

  if (!chiedo) {
    return (
      <section className="riquadro">
        <button type="button" className="piatto" onClick={() => setChiedo(true)}>
          Disattiva questo socio
        </button>
      </section>
    );
  }

  return (
    <section className="conferma">
      <p className="tenue piccolo">
        Archiviare <strong>{socio.displayName}</strong> lo toglie dagli elenchi e libera il suo telefono e la
        sua email. <strong>Non cancella niente</strong>: le partite, gli incassi e i movimenti restano dove
        sono. Si può rimettere dentro quando si vuole.
      </p>
      <Guasto errore={gesto.error} />
      <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('archive')}>
        {gesto.isPending ? 'Un attimo…' : 'Sì, archivialo'}
      </button>
      <button type="button" className="piatto" onClick={() => setChiedo(false)}>
        Lascia stare
      </button>
    </section>
  );
}

// ── Cancellare ──────────────────────────────────────────────────────────────
//
// 🗑️ 28/09/2026, mockup «Correggi e cancella»: *«1A 2A 3A»*. Si cancella solo una scheda
// SENZA STORIA (2A): un doppione, un errore, una prova, chi si è registrato dal bot e non ha mai
// giocato. Chi ha già partite, pagamenti o movimenti si archivia, e i conti restano completi.
// La storia la chiede il tocco, e il server la rilegge prima di cancellare: qui si spiega, là si
// decide.

/** 🛡️ 22/10/2026 (mockup «Cancellazione e Level Akinator», A): il bot le toglie da Level Akinator. */
const ANCHE_DA_LEVEL_AKINATOR =
  'Spariscono anche da Level Akinator, entro qualche minuto, le risposte alle domande del livello e le valutazioni della Segreteria.';

function Cancellazione({
  socio,
  anche,
  chiudi,
}: {
  socio: Socio;
  /** Il titolare: può cancellare anche una scheda con la storia. */
  anche: boolean;
  chiudi: () => void;
}): React.JSX.Element {
  const [chiedo, setChiedo] = useState(false);
  const storia = usaStoriaDelSocio(socio.id, chiedo);
  const gesto = usaCancellazioneSocio(socio.id);
  const archivia = usaArchiviazione(socio.id);
  const chiesta = usaCancellazioneChiesta(socio.id).data?.requestedAt ?? null;
  // 🛡️ 22/10/2026 (mockup «La cancellazione chiesta dal socio», A): l'ha chiesta lui, dal bot.
  const dalBot = chiesta && (
    <p className="piccolo">
      🛡️ Cancellazione chiesta dal bot il {quandoIscritto(chiesta)}: {socio.displayName} chiede di cancellare
      i suoi dati.
    </p>
  );
  const lasciaStare = (
    <button type="button" className="piatto" onClick={() => setChiedo(false)}>
      Lascia stare
    </button>
  );

  if (!chiedo) {
    return (
      <section className="riquadro">
        {dalBot}
        <button type="button" className="piatto pericolo" onClick={() => setChiedo(true)}>
          Cancella questo socio
        </button>
      </section>
    );
  }
  if (storia.isPending) {
    return (
      <section className="conferma">
        <Attesa cosa="la storia del socio" />
      </section>
    );
  }
  if (!storia.data) {
    return (
      <section className="conferma">
        <Guasto errore={storia.error} />
        {lasciaStare}
      </section>
    );
  }

  if (!storia.data.deletable) {
    const archiviato = socio.archivedAt !== null;
    return (
      <section className="conferma">
        <p className="tenue piccolo">
          <strong>{socio.displayName}</strong> ha già una storia al circolo: {laStoria(storia.data)}. Questi
          dati restano per la contabilità, quindi la scheda si tiene.
        </p>
        <p className="tenue piccolo">
          {archiviato
            ? 'La scheda è già archiviata: resta fuori dagli elenchi, e telefono ed email sono liberi.'
            : 'Puoi archiviare la scheda: sparisce dagli elenchi e libera telefono ed email, e se torna la rimetti fra i soci con un tocco.'}
        </p>
        <Guasto errore={archivia.error} />
        {!archiviato && (
          <button
            type="button"
            disabled={archivia.isPending}
            onClick={() => archivia.mutate('archive', { onSuccess: () => setChiedo(false) })}
          >
            {archivia.isPending ? 'Un attimo…' : 'Archivia la scheda'}
          </button>
        )}
        {anche && <CancellaConLaStoria socio={socio} chiudi={chiudi} />}
        {lasciaStare}
      </section>
    );
  }

  return (
    <section className="conferma">
      <p className="tenue piccolo">
        Cancellare <strong>{socio.displayName}</strong> toglie la sua scheda <strong>per sempre</strong>:
        nome, telefono, email, livello, preferenze e il collegamento al bot. Non ha partite, pagamenti o
        movimenti, quindi non resta niente in sospeso.
      </p>
      <p className="tenue piccolo">{ANCHE_DA_LEVEL_AKINATOR}</p>
      <p className="tenue piccolo">
        Non si può rimettere indietro. Se pensi che possa tornare, archivia la scheda.
      </p>
      <Guasto errore={gesto.error} />
      <button
        type="button"
        className="pericolo"
        disabled={gesto.isPending}
        onClick={() => gesto.mutate(undefined, { onSuccess: chiudi })}
      >
        {gesto.isPending ? 'Un attimo…' : 'Sì, cancella la scheda per sempre'}
      </button>
      {lasciaStare}
    </section>
  );
}

/**
 * 🗑️ Il titolare cancella anche una scheda con la storia (29/09/2026, mockup «Cancellare un socio»:
 * *«1 A - 2 Solo il titolare - 3 si»*). Via la persona, restano i conti a nome «Socio cancellato».
 * ⭐ Scelta 3: il bottone si accende solo col nome del socio scritto per intero, e il gestionale lo
 * ricontrolla.
 */
function CancellaConLaStoria({ socio, chiudi }: { socio: Socio; chiudi: () => void }): React.JSX.Element {
  const [nome, setNome] = useState('');
  const gesto = usaCancellazioneConStoria(socio.id);
  const giusto = stessoNome(nome, socio.displayName);
  return (
    <div className="conferma">
      <p className="tenue piccolo">
        <strong>Cancella comunque</strong>, come titolare: nome, telefono, email e collegamento al bot di{' '}
        <strong>{socio.displayName}</strong> spariscono per sempre. Le partite, i pagamenti e i movimenti
        restano per i conti, a nome «Socio cancellato», e il saldo del borsellino va a zero. Non si può
        rimettere indietro.
      </p>
      <p className="tenue piccolo">{ANCHE_DA_LEVEL_AKINATOR}</p>
      <Campo
        etichetta={`Per confermare scrivi il nome del socio: ${socio.displayName}`}
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        autoComplete="off"
      />
      <Guasto errore={gesto.error} />
      <button
        type="button"
        className="pericolo"
        disabled={!giusto || gesto.isPending}
        onClick={() => gesto.mutate(nome, { onSuccess: chiudi })}
      >
        {gesto.isPending ? 'Un attimo…' : `Sì, cancella ${socio.displayName} per sempre`}
      </button>
    </div>
  );
}

/** «12 partite, 3 pagamenti e 5 movimenti del borsellino»: solo quello che c'è. */
function laStoria(s: StoriaDelSocio): string {
  const conta = (n: number, uno: string, tanti: string) => (n > 0 ? `${n} ${n === 1 ? uno : tanti}` : null);
  const pezzi = [
    conta(s.matches, 'partita', 'partite'),
    conta(s.payments, 'pagamento', 'pagamenti'),
    conta(s.walletMovements, 'movimento del borsellino', 'movimenti del borsellino'),
    conta(s.credits, 'Credito da organizzatore', 'Crediti da organizzatore'),
    conta(s.deposits, 'acconto', 'acconti'),
    conta(s.courses, 'corso', 'corsi'),
    conta(s.noShows, 'campo vuoto', 'campi vuoti'),
  ].filter((p): p is string => p !== null);
  return pezzi.length > 1 ? `${pezzi.slice(0, -1).join(', ')} e ${pezzi.at(-1)}` : (pezzi[0] ?? '');
}

/** «28/09 alle 18:42», nel fuso del circolo. */
export function quandoIscritto(iso: string): string {
  const d = new Date(iso);
  const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Rome' });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
  return `${giorno} alle ${ora}`;
}

/**
 * 🧪 **«Azzera il livello», solo per le prove** (14/10/2026, mockup «Azzera il livello», *«vai»*).
 * 🗣️ Maurizio Aprea: *«Azzeriamo il mio livello di gioco. Con un bottone, solo sul mio nome»* ·
 * *«solo nel gestionale di test»*. Toglie il livello e le prove consegnate: sul telefono, con AVVIA,
 * il bot chiede di nuovo il livello. Se il riquadro c'è lo dice il gestionale (il titolare, questa
 * scheda, il bot di prova): nel gestionale dei soci non compare.
 */
function AzzeraIlLivello({ socio }: { socio: Socio }): React.JSX.Element | null {
  const disponibile = usaAzzeraLivelloDisponibile(socio.id);
  const gesto = usaAzzeraLivello(socio.id);
  const [chiedo, setChiedo] = useState(false);
  if (!disponibile.data?.available) return null;
  return (
    <section className="riquadro">
      <h4>🧪 Solo per le prove</h4>
      <p className="tenue piccolo">
        Rifai il giro del livello dal telefono, come un nuovo registrato. Partita, rubrica e nome restano.
      </p>
      {gesto.isSuccess && !chiedo && (
        <p className="piccolo">
          ✅ Fatto: il livello è azzerato. Sul telefono tocca AVVIA per rifare il giro.
        </p>
      )}
      <Guasto errore={gesto.error} />
      {chiedo ? (
        <div className="conferma">
          <p className="tenue piccolo">
            Azzeri il livello di <strong>{socio.displayName}</strong>? Il livello e le risposte già date si
            tolgono. Sul telefono, con AVVIA, rifai il giro del livello.
          </p>
          <button
            type="button"
            className="pericolo"
            disabled={gesto.isPending}
            onClick={() => gesto.mutate(undefined, { onSuccess: () => setChiedo(false) })}
          >
            {gesto.isPending ? 'Un attimo…' : 'Azzera'}
          </button>
          <button type="button" className="piatto" onClick={() => setChiedo(false)}>
            Annulla
          </button>
        </div>
      ) : (
        <button type="button" className="piatto pericolo" onClick={() => setChiedo(true)}>
          🔄 Azzera il livello
        </button>
      )}
      <NotificheDiProva socio={socio} />
    </section>
  );
}

/**
 * 🧪 «Prepara le notifiche di prova» (30/10/2026, mockup «Le notifiche di prova», A), nello stesso riquadro: c'è solo
 * dove c'è «Azzera il livello» (il titolare, la scheda giusta, il bot di prova).
 */
function NotificheDiProva({ socio }: { socio: Socio }): React.JSX.Element {
  const gesto = usaNotificheDiProva(socio.id);
  return (
    <div className="notifiche-di-prova">
      <p className="tenue piccolo">
        Prepara sul Tuo telefono partite, lezioni e notifiche da guardare. Tocca di nuovo per ricominciare da
        capo.
      </p>
      {gesto.isSuccess && (
        <p className="piccolo">✅ Fatto: sul telefono arriva la sezione delle notifiche.</p>
      )}
      <Guasto errore={gesto.error} />
      <button type="button" className="piatto" disabled={gesto.isPending} onClick={() => gesto.mutate()}>
        {gesto.isPending ? 'Un attimo…' : 'Prepara le notifiche di prova'}
      </button>
    </div>
  );
}
